import React from 'react';
import { XIcon, FileTextIcon, ShoppingCartIcon, ReceiptIcon, CreditCardIcon, DollarSignIcon, UserIcon, CalendarIcon, ClockIcon, LinkIcon, ExternalLinkIcon, BuildingIcon, InfoIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, HashIcon, TagIcon } from 'lucide-react';
import { LedgerEntry, DocumentType, LedgerState } from './LedgerView';
import { formatAmount } from '../utils/currencyFormatter';
interface LedgerEntryDetailProps {
  entry: LedgerEntry;
  onClose: () => void;
  locale: string;
}
export function LedgerEntryDetail({
  entry,
  onClose,
  locale
}: LedgerEntryDetailProps) {
  // Get document type details
  const getDocumentTypeDetails = (type: DocumentType) => {
    switch (type) {
      case 'Invoice':
        return {
          icon: <ReceiptIcon className="h-5 w-5" data-id="element-8879" />,
          color: 'text-purple-600',
          bgColor: 'bg-purple-100',
          label: 'Invoice'
        };
      case 'Purchase Order':
        return {
          icon: <ShoppingCartIcon className="h-5 w-5" data-id="element-8880" />,
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          label: 'Purchase Order'
        };
      case 'Credit Note':
        return {
          icon: <FileTextIcon className="h-5 w-5" data-id="element-8881" />,
          color: 'text-orange-600',
          bgColor: 'bg-orange-100',
          label: 'Credit Note'
        };
      case 'Expense Claim':
        return {
          icon: <CreditCardIcon className="h-5 w-5" data-id="element-8882" />,
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          label: 'Expense Claim'
        };
      case 'Contract':
        return {
          icon: <FileTextIcon className="h-5 w-5" data-id="element-8883" />,
          color: 'text-indigo-600',
          bgColor: 'bg-indigo-100',
          label: 'Contract'
        };
      case 'Payment':
        return {
          icon: <DollarSignIcon className="h-5 w-5" data-id="element-8884" />,
          color: 'text-emerald-600',
          bgColor: 'bg-emerald-100',
          label: 'Payment'
        };
      default:
        return {
          icon: <FileTextIcon className="h-5 w-5" data-id="element-8885" />,
          color: 'text-gray-600',
          bgColor: 'bg-gray-100',
          label: 'Document'
        };
    }
  };
  const documentDetails = getDocumentTypeDetails(entry.documentType);
  // Get ledger state details
  const getLedgerStateDetails = (state: LedgerState) => {
    switch (state) {
      case 'Informational':
        return {
          icon: <InfoIcon className="h-5 w-5" data-id="element-8886" />,
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-800',
          label: 'Informational',
          description: 'Draft entry - not yet impacting financial exposure'
        };
      case 'Active':
        return {
          icon: <CheckCircleIcon className="h-5 w-5" data-id="element-8887" />,
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          label: 'Active',
          description: 'Active entry impacting financial exposure'
        };
      case 'Reversed':
        return {
          icon: <XCircleIcon className="h-5 w-5" data-id="element-8888" />,
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          label: 'Reversed',
          description: 'Entry has been reversed'
        };
      default:
        return {
          icon: <InfoIcon className="h-5 w-5" data-id="element-8889" />,
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          label: state,
          description: ''
        };
    }
  };
  const stateDetails = getLedgerStateDetails(entry.ledgerState);
  const isReversal = entry.ledgerEntryType === 'Reversal';
  return <div className="fixed inset-0 overflow-hidden z-20" aria-labelledby="slide-over-title" role="dialog" aria-modal="true" data-id="element-8890">
      <div className="absolute inset-0 overflow-hidden" data-id="element-8891">
        {/* Background overlay */}
        <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose} data-id="element-8892"></div>
        <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-8893">
          <div className="relative w-screen max-w-2xl" data-id="element-8894">
            <div className="h-full flex flex-col py-6 bg-white shadow-xl overflow-y-auto" data-id="element-8895">
              {/* Header */}
              <div className="px-4 sm:px-6" data-id="element-8896">
                <div className="flex items-start justify-between" data-id="element-8897">
                  <h2 className="text-lg font-medium text-gray-900" id="slide-over-title" data-id="element-8898">
                    Operational Ledger Entry
                  </h2>
                  <div className="ml-3 h-7 flex items-center" data-id="element-8899">
                    <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" onClick={onClose} data-id="element-8900">
                      <span className="sr-only" data-id="element-8901">Close panel</span>
                      <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-8902" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Main content */}
              <div className="mt-6 relative flex-1 px-4 sm:px-6" data-id="element-8903">
                {/* Entry ID and State */}
                <div className="border-b border-gray-200 pb-5 mb-5" data-id="element-8904">
                  <div className="flex justify-between items-start" data-id="element-8905">
                    <div data-id="element-8906">
                      <h3 className="text-xl font-semibold text-gray-900" data-id="element-8907">
                        {entry.documentNumber}
                      </h3>
                      <p className="mt-1 text-sm text-gray-500 font-mono" data-id="element-8908">
                        Entry ID: {entry.ledgerEntryId}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${stateDetails.bgColor} ${stateDetails.textColor}`} data-id="element-8909">
                      {stateDetails.icon}
                      <span className="ml-1" data-id="element-8910">{stateDetails.label}</span>
                    </span>
                  </div>
                  {stateDetails.description && <p className="mt-2 text-sm text-gray-600" data-id="element-8911">
                      {stateDetails.description}
                    </p>}
                </div>

                {/* Reversal Warning */}
                {isReversal && entry.reversesLedgerEntryId && <div className="mb-6 bg-orange-50 border-l-4 border-orange-400 p-4" data-id="element-8912">
                    <div className="flex" data-id="element-8913">
                      <AlertCircleIcon className="h-5 w-5 text-orange-400" data-id="element-8914" />
                      <div className="ml-3" data-id="element-8915">
                        <h3 className="text-sm font-medium text-orange-800" data-id="element-8916">
                          Reversal Entry
                        </h3>
                        <p className="mt-1 text-sm text-orange-700" data-id="element-8917">
                          This entry reverses ledger entry:{' '}
                          <span className="font-mono" data-id="element-8918">
                            {entry.reversesLedgerEntryId}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>}

                {/* Document information */}
                <div className="mb-6" data-id="element-8919">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8920">
                    DOCUMENT INFORMATION
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-8921">
                    <div className="flex items-center mb-3" data-id="element-8922">
                      <div className={`p-2 rounded-md ${documentDetails.bgColor} ${documentDetails.color} mr-3`} data-id="element-8923">
                        {documentDetails.icon}
                      </div>
                      <div data-id="element-8924">
                        <p className="text-sm font-medium text-gray-900" data-id="element-8925">
                          {documentDetails.label}
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-8926">
                          Status: {entry.documentStatus}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-8927">
                      <div data-id="element-8928">
                        <p className="text-xs text-gray-500" data-id="element-8929">Document ID</p>
                        <p className="font-mono text-gray-900" data-id="element-8930">
                          {entry.documentId}
                        </p>
                      </div>
                      <div data-id="element-8931">
                        <p className="text-xs text-gray-500" data-id="element-8932">Entry Type</p>
                        <p className="text-gray-900" data-id="element-8933">{entry.ledgerEntryType}</p>
                      </div>
                    </div>
                    <div className="mt-2 flex items-center" data-id="element-8934">
                      <LinkIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8935" />
                      <a href="#" className="text-sm text-indigo-600 hover:text-indigo-900 flex items-center" data-id="element-8936">
                        View Document{' '}
                        <ExternalLinkIcon className="h-3 w-3 ml-1" data-id="element-8937" />
                      </a>
                    </div>
                  </div>
                </div>

                {/* Organisational Context */}
                <div className="mb-6" data-id="element-8938">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8939">
                    ORGANISATIONAL CONTEXT
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-8940">
                    <div className="flex items-start mb-2" data-id="element-8941">
                      <BuildingIcon className="h-4 w-4 text-gray-400 mr-2 mt-0.5" data-id="element-8942" />
                      <div className="flex-1" data-id="element-8943">
                        <p className="text-xs text-gray-500" data-id="element-8944">Org Unit</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-8945">
                          {entry.orgUnitName}
                        </p>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-8946">
                          Path: {entry.orgUnitPath}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Financial Information */}
                <div className="mb-6" data-id="element-8947">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8948">
                    FINANCIAL IMPACT
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-8949">
                    <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-8950">
                      <div className="bg-green-50 rounded-lg p-3 border border-green-200" data-id="element-8951">
                        <p className="text-xs text-green-600 font-medium mb-1" data-id="element-8952">
                          Debit (Increases Exposure)
                        </p>
                        <p className="text-xl font-mono font-bold text-green-700" data-id="element-8953">
                          {entry.debitAmount ? formatAmount(entry.debitAmount, locale) : '-'}
                        </p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-3 border border-red-200" data-id="element-8954">
                        <p className="text-xs text-red-600 font-medium mb-1" data-id="element-8955">
                          Credit (Reduces Exposure)
                        </p>
                        <p className="text-xl font-mono font-bold text-red-700" data-id="element-8956">
                          {entry.creditAmount ? formatAmount(entry.creditAmount, locale) : '-'}
                        </p>
                      </div>
                    </div>
                    {entry.calculatedRunningBalance !== undefined && <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200" data-id="element-8957">
                        <p className="text-xs text-indigo-600 font-medium mb-1" data-id="element-8958">
                          Running Balance (Calculated)
                        </p>
                        <p className="text-xl font-mono font-bold text-indigo-700" data-id="element-8959">
                          {formatAmount(entry.calculatedRunningBalance, locale)}
                        </p>
                        <p className="text-xs text-indigo-600 mt-1" data-id="element-8960">
                          Display Precision: {entry.displayPrecision} decimals
                        </p>
                      </div>}
                  </div>
                </div>

                {/* GL Coding */}
                <div className="mb-6" data-id="element-8961">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8962">
                    GL CODING & ALLOCATION
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4" data-id="element-8963">
                    <div className="flex items-center" data-id="element-8964">
                      <HashIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8965" />
                      <div data-id="element-8966">
                        <p className="text-xs text-gray-500" data-id="element-8967">GL Code</p>
                        <p className="text-sm font-mono font-medium text-gray-900" data-id="element-8968">
                          {entry.glCode}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Counterparty */}
                {(entry.vendorName || entry.userName) && <div className="mb-6" data-id="element-8969">
                    <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8970">
                      COUNTERPARTY
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4" data-id="element-8971">
                      <div className="flex items-center" data-id="element-8972">
                        <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8973" />
                        <div data-id="element-8974">
                          <p className="text-xs text-gray-500" data-id="element-8975">
                            {entry.vendorName ? 'Vendor' : 'User'}
                          </p>
                          <p className="text-sm font-medium text-gray-900" data-id="element-8976">
                            {entry.vendorName || entry.userName}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>}

                {/* Audit and Traceability */}
                <div className="mb-6" data-id="element-8977">
                  <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-8978">
                    AUDIT & TRACEABILITY
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3" data-id="element-8979">
                    <div className="flex items-center" data-id="element-8980">
                      <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8981" />
                      <div data-id="element-8982">
                        <p className="text-xs text-gray-500" data-id="element-8983">Created Date</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-8984">
                          {new Date(entry.createdDate).toLocaleString(locale, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-8985">
                      <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8986" />
                      <div data-id="element-8987">
                        <p className="text-xs text-gray-500" data-id="element-8988">Created By</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-8989">
                          {entry.createdBy}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-8990">
                      <TagIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8991" />
                      <div data-id="element-8992">
                        <p className="text-xs text-gray-500" data-id="element-8993">Source System</p>
                        <p className="text-sm font-medium text-gray-900" data-id="element-8994">
                          {entry.sourceSystem}
                        </p>
                      </div>
                    </div>
                    {entry.erpReference && <div className="flex items-center" data-id="element-8995">
                        <LinkIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-8996" />
                        <div data-id="element-8997">
                          <p className="text-xs text-gray-500" data-id="element-8998">ERP Reference</p>
                          <p className="text-sm font-mono font-medium text-gray-900" data-id="element-8999">
                            {entry.erpReference}
                          </p>
                        </div>
                      </div>}
                  </div>
                </div>

                {/* Notes */}
                {entry.reasonOrNotes && <div className="mb-6" data-id="element-9000">
                    <h4 className="text-sm font-medium text-gray-500 mb-2" data-id="element-9001">
                      REASON / NOTES
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4" data-id="element-9002">
                      <p className="text-sm text-gray-900" data-id="element-9003">
                        {entry.reasonOrNotes}
                      </p>
                    </div>
                  </div>}

                {/* Read-only Notice */}
                <div className="mt-6 bg-blue-50 border-l-4 border-blue-400 p-4" data-id="element-9004">
                  <div className="flex" data-id="element-9005">
                    <InfoIcon className="h-5 w-5 text-blue-400" data-id="element-9006" />
                    <div className="ml-3" data-id="element-9007">
                      <p className="text-sm text-blue-700" data-id="element-9008">
                        <strong data-id="element-9009">Read-Only Ledger:</strong> This entry is
                        append-only and cannot be modified. Corrections are made
                        through reversing entries.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}